import { useMemo, useState } from 'react';
import {
  Box, Chip, Grid, IconButton, Paper, Stack, TextField, Typography, alpha, Button, InputAdornment, MenuItem,
} from '@mui/material';
import { Add, Remove, Search, Handyman } from '@mui/icons-material';
import { formatMoney } from '../../utils/currency';
import { resolveAssetUrl } from '../../utils/assetUrl';

export type OnDemandCategory = {
  id?: string;
  _id?: string;
  code?: string;
  name: string;
  parentId?: string | null;
  parentCode?: string | null;
};

export type OnDemandPackage = {
  _id?: string;
  id?: string;
  name?: string;
  description?: string;
  categoryId?: string;
  categoryCode?: string;
  durationMin?: number;
  basePrice?: number;
  visitCharge?: number;
  displayPrice?: number;
  thumbnailUrl?: string;
  iconUrl?: string;
  bannerUrl?: string;
  inclusions?: string[];
  pricingModel?: string;
};

export type OnDemandCartLine = { packageId: string; quantity: number };

type Props = {
  accent: string;
  categories: OnDemandCategory[];
  packages: OnDemandPackage[];
  cart: OnDemandCartLine[];
  onCartChange: (cart: OnDemandCartLine[]) => void;
  scheduledAt: string;
  slotIso: string;
  slots: Array<{ start: string; label?: string; available?: boolean }>;
  onScheduleChange: (scheduledAt: string, slotIso?: string) => void;
  fieldErrors?: Record<string, string>;
};

function idOf(row: { id?: string; _id?: string }): string {
  return String(row.id || row._id || '');
}

function packageIdOf(row: OnDemandPackage): string {
  return String(row._id || row.id || '');
}

function priceOf(pkg: OnDemandPackage): number {
  return Number(pkg.displayPrice ?? pkg.basePrice ?? 0);
}

export default function DispatchOnDemandServicePicker({
  accent, categories, packages, cart, onCartChange,
  scheduledAt, slotIso, slots, onScheduleChange, fieldErrors = {},
}: Props) {
  const [query, setQuery] = useState('');
  const [parentId, setParentId] = useState('');
  const [subId, setSubId] = useState('');

  const parents = useMemo(
    () => categories.filter((c) => !c.parentId && !c.parentCode),
    [categories],
  );

  const children = useMemo(() => {
    if (!parentId) return [];
    return categories.filter((c) => String(c.parentId || '') === parentId);
  }, [categories, parentId]);

  const filteredPackages = useMemo(() => {
    const q = query.trim().toLowerCase();
    const activeCat = subId || parentId;
    return packages.filter((p) => {
      const pid = packageIdOf(p);
      if (!pid) return false;
      if (activeCat) {
        const catMatch = String(p.categoryId || '') === activeCat
          || categories.some((c) => idOf(c) === activeCat && (c.code === p.categoryCode || idOf(c) === String(p.categoryId || '')));
        if (!catMatch && p.categoryId) {
          // Also allow packages whose category is a child of selected parent
          const pkgCat = categories.find((c) => idOf(c) === String(p.categoryId || '') || c.code === p.categoryCode);
          if (pkgCat) {
            if (subId && idOf(pkgCat) !== subId) return false;
            if (!subId && parentId && String(pkgCat.parentId || '') !== parentId && idOf(pkgCat) !== parentId) return false;
          }
        } else if (activeCat && p.categoryId && !catMatch) {
          return false;
        }
      }
      if (!q) return true;
      return (
        (p.name || '').toLowerCase().includes(q)
        || (p.description || '').toLowerCase().includes(q)
        || (p.categoryCode || '').toLowerCase().includes(q)
      );
    });
  }, [packages, query, parentId, subId, categories]);

  const cartRows = useMemo(() => cart.map((line) => {
    const pkg = packages.find((p) => packageIdOf(p) === line.packageId);
    return { ...line, pkg, lineTotal: (pkg ? priceOf(pkg) : 0) * line.quantity };
  }).filter((r) => r.pkg), [cart, packages]);

  const cartTotal = cartRows.reduce((s, r) => s + r.lineTotal, 0);

  const setQty = (packageId: string, quantity: number) => {
    const next = cart.filter((c) => c.packageId !== packageId);
    if (quantity > 0) next.push({ packageId, quantity });
    onCartChange(next);
  };

  return (
    <Stack spacing={1.25} sx={{ minHeight: 0 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search services…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
        }}
      />

      {parents.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="caption" fontWeight={800} color="text.secondary" letterSpacing="0.06em">
            CATEGORY
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label="All"
              clickable
              color={!parentId ? 'primary' : 'default'}
              variant={!parentId ? 'filled' : 'outlined'}
              onClick={() => { setParentId(''); setSubId(''); }}
              sx={!parentId ? { bgcolor: accent } : undefined}
            />
            {parents.map((c) => {
              const id = idOf(c);
              const active = parentId === id;
              return (
                <Chip
                  key={id}
                  size="small"
                  label={c.name}
                  clickable
                  color={active ? 'primary' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  onClick={() => { setParentId(id); setSubId(''); }}
                  sx={active ? { bgcolor: accent } : undefined}
                />
              );
            })}
          </Stack>
          {children.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip size="small" label="All sub" clickable variant={!subId ? 'filled' : 'outlined'}
                color={!subId ? 'primary' : 'default'}
                onClick={() => setSubId('')}
                sx={!subId ? { bgcolor: alpha(accent, 0.85) } : undefined}
              />
              {children.map((c) => {
                const id = idOf(c);
                const active = subId === id;
                return (
                  <Chip
                    key={id}
                    size="small"
                    label={c.name}
                    clickable
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    onClick={() => setSubId(id)}
                    sx={active ? { bgcolor: accent } : undefined}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>
      )}

      {fieldErrors.cart && (
        <Typography variant="caption" color="error" fontWeight={700}>{fieldErrors.cart}</Typography>
      )}

      <Typography variant="caption" fontWeight={800} color="text.secondary" letterSpacing="0.06em">
        SERVICE PACKAGES
      </Typography>
      <Box sx={{
        maxHeight: 240, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px', bgcolor: '#f8fafc', p: 1,
      }}>
        {filteredPackages.length === 0 ? (
          <Stack alignItems="center" py={3} color="text.secondary">
            <Handyman sx={{ mb: 1, opacity: 0.45 }} />
            <Typography variant="body2">No packages in this category</Typography>
          </Stack>
        ) : (
          <Grid container spacing={1}>
            {filteredPackages.map((pkg) => {
              const pid = packageIdOf(pkg);
              const qty = cart.find((c) => c.packageId === pid)?.quantity || 0;
              const img = resolveAssetUrl(pkg.thumbnailUrl || pkg.iconUrl || pkg.bannerUrl || '');
              return (
                <Grid item xs={12} sm={6} key={pid}>
                  <Paper elevation={0} sx={{
                    p: 1, display: 'flex', gap: 1, borderRadius: '10px', height: '100%',
                    border: `1px solid ${qty > 0 ? alpha(accent, 0.45) : '#e2e8f0'}`,
                    bgcolor: qty > 0 ? alpha(accent, 0.06) : '#fff',
                  }}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: '8px', flexShrink: 0, bgcolor: '#e2e8f0',
                      backgroundImage: img ? `url(${img})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary',
                    }}>
                      {!img ? <Handyman fontSize="small" /> : null}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} noWrap>{pkg.name || 'Service'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[
                          pkg.durationMin != null ? `${pkg.durationMin} min` : null,
                          pkg.pricingModel || null,
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.5}>
                        <Typography variant="body2" fontWeight={800}>{formatMoney(priceOf(pkg))}</Typography>
                        {qty === 0 ? (
                          <Button size="small" variant="outlined" onClick={() => setQty(pid, 1)}
                            sx={{ minWidth: 0, px: 1.1, py: 0.1, borderRadius: '8px', borderColor: accent, color: accent, fontWeight: 800, fontSize: 11 }}>
                            ADD
                          </Button>
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={0.2} sx={{
                            border: `1px solid ${alpha(accent, 0.35)}`, borderRadius: '8px', bgcolor: '#fff',
                          }}>
                            <IconButton size="small" onClick={() => setQty(pid, qty - 1)} sx={{ color: accent }}>
                              <Remove fontSize="small" />
                            </IconButton>
                            <Typography variant="caption" fontWeight={800} sx={{ minWidth: 14, textAlign: 'center' }}>{qty}</Typography>
                            <IconButton size="small" onClick={() => setQty(pid, qty + 1)} sx={{ color: accent }}>
                              <Add fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {cartRows.length > 0 && (
        <Paper elevation={0} sx={{
          p: 1.25, borderRadius: '12px', border: `1px solid ${alpha(accent, 0.25)}`, bgcolor: alpha(accent, 0.04),
        }}>
          <Typography variant="caption" fontWeight={800} color="text.secondary" letterSpacing="0.06em">
            SERVICES CART · {cartRows.reduce((n, r) => n + r.quantity, 0)}
          </Typography>
          <Stack spacing={0.5} mt={0.75}>
            {cartRows.map((row) => (
              <Stack key={row.packageId} direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {row.pkg?.name} × {row.quantity}
                </Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoney(row.lineTotal, row)}</Typography>
              </Stack>
            ))}
            <Stack direction="row" justifyContent="space-between" pt={0.5} sx={{ borderTop: '1px dashed #cbd5e1' }}>
              <Typography variant="body2" fontWeight={800}>Subtotal</Typography>
              <Typography variant="body2" fontWeight={800} color={accent}>{formatMoney(cartTotal)}</Typography>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Grid container spacing={1}>
        <Grid item xs={12} sm={slots.length ? 7 : 12}>
          <TextField fullWidth size="small" type="datetime-local" required label="Schedule"
            InputLabelProps={{ shrink: true }}
            value={scheduledAt || ''}
            error={!!fieldErrors.scheduledAt}
            helperText={fieldErrors.scheduledAt}
            onChange={(e) => onScheduleChange(e.target.value, '')} />
        </Grid>
        {slots.length > 0 && (
          <Grid item xs={12} sm={5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Slot"
              value={slotIso || ''}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => onScheduleChange(scheduledAt, e.target.value)}
            >
              <MenuItem value="">Use schedule</MenuItem>
              {slots.map((s) => (
                <MenuItem key={s.start} value={s.start} disabled={s.available === false}>
                  {s.label || s.start}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>
    </Stack>
  );
}
